import React, { useState } from 'react';
import { MessageCircleOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Empty } from '@/v2/design-system/primitives';
import { Button } from '@/v2/design-system/primitives';
import { useWhatsAppAccess } from '@/hooks/useWhatsAppAccess';
import { useAuthStore } from '@/stores/auth';
import { useSingleFlightRunner } from '@/hooks/useSingleFlightCallback';

function errorMessage(error: unknown) {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim()) return message;
  return 'Não foi possível ativar o WhatsApp. Tente novamente.';
}

export function WhatsAppGated({ children }: { children: React.ReactNode }) {
  // Story 65.37: a gravação desta tela roda uma vez por ação enquanto estiver a caminho.
  const once = useSingleFlightRunner();
  const { enabled } = useWhatsAppAccess();
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (enabled) return <>{children}</>;

  const handleRequest = () => once('handleRequest', async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await api.post('/whatsapp/request-access');
      const { data } = await api.get('/auth/me');
      setUser(data);
      await queryClient.invalidateQueries({ queryKey: ['whatsapp-status'] });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="-m-4 grid h-dvh-minus-topbar place-items-center lg:-m-8">
      <Empty
        icon={<MessageCircleOff />}
        title="WhatsApp ainda não está ativo na sua clínica"
        description="O clique cria a instância da clínica e em seguida mostra o QR para conectar o número."
        action={
          <div className="flex flex-col items-center gap-2">
            <Button onClick={() => void handleRequest()} disabled={loading}>
              {loading ? 'Ativando…' : 'Ativar WhatsApp'}
            </Button>
            {error ? (
              <p className="max-w-sm text-center text-so-13 text-sober-red" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        }
      />
    </div>
  );
}
